// src/components/resources/calculators/video-dropoff/components/ResultsSection.tsx

/**
 * @file Results Section Component
 * @description Results display component for video dropoff calculator analysis
 */

import { logClientError } from '~/lib/logger-client'
import { useEffect, useMemo } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { cn } from '~/lib/utils'
import RiLineChartLine from "~/icons/remix/generated/RiLineChartLine"
import RiBarChartLine from "~/icons/remix/generated/RiBarChartLine"
import RiPercentLine from "~/icons/remix/generated/RiPercentLine"
import RiTimeLine from "~/icons/remix/generated/RiTimeLine"
import RiVideoLine from "~/icons/remix/generated/RiVideoLine"
import RiInformationLine from "~/icons/remix/generated/RiInformationLine"
import RiFacebookCircleLine from "~/icons/remix/generated/RiFacebookCircleLine"
import RiTiktokLine from "~/icons/remix/generated/RiTiktokLine"
import RiYoutubeLine from "~/icons/remix/generated/RiYoutubeLine"
import RiVimeoLine from "~/icons/remix/generated/RiVimeoLine"
import RiLinksLine from "~/icons/remix/generated/RiLinksLine"
import { RetentionChart, DropoffChart, NormalizedChart } from '~/components/resources/calculators/video-dropoff/components/charts'
import type { 
  ChartView, 
  CalculatorResults, 
  CalculatorInputs,
  RetentionDataPoint,
  DropoffDataPoint
} from '~/components/resources/calculators/video-dropoff/types'

/**
 * A published completion-rate benchmark band for a platform + length segment.
 */
interface CompletionBenchmark {
  /** Lower bound of the published typical range (%) */
  min: number
  /** Upper bound of the published typical range (%) */
  max: number
  /** Human-readable range, e.g. "40–65%" */
  rangeText: string
  /** Placement/length segment the range describes */
  segmentLabel: string
  /**
   * Caveat appended to the verdict when the published band is
   * placement-specific but the tool only collects platform, so results
   * from other placements shouldn't be read as failing (`r3540891743`).
   */
  placementNote?: string
  /** Compact form of the placement caveat for the metric-card caption. */
  captionNote?: string
}

/**
 * Published completion-rate benchmark bands by platform and video length.
 *
 * Single source of truth: the VCR glossary entry's `segmentBenchmarks` in
 * `data/glossary.yaml` (Sprout Social 2025 / Socialinsider 2025; OpusClip
 * 2025 / WebFX; OwlClaw YouTube Benchmarks 2026; Store Growers 2026 /
 * Mutesix 2025), mirrored by the Video Completion Rate Calculator FAQ in
 * `src/lib/resources/data/calculators/landingVariants.ts`. Keep those
 * surfaces in sync when updating ranges — this page renders both the FAQ
 * and these verdicts, so they must agree. `r3540594591`
 *
 * Returns `null` when no published band exists for the combination
 * (e.g. Vimeo, custom sources) — the verdict copy then avoids claiming a
 * numeric "platform average" it can't source.
 */
const getCompletionBenchmark = (
  platform: CalculatorInputs['platform'],
  videoLength: number
): CompletionBenchmark | null => {
  switch (platform) {
    case 'facebook':
      if (videoLength < 15) {
        return {
          min: 75,
          max: 100,
          rangeText: '75–100%',
          segmentLabel: 'Meta short-form under 15 seconds',
          placementNote:
            'That band reflects Reels/short-form reporting — feed prospecting placements can run lower, so compare against the placement you actually ran.',
          captionNote: 'Reels/short-form reporting basis — feed prospecting can run lower',
        }
      }
      if (videoLength <= 30) {
        // The published 40–65% band is Reels/Stories-specific, but this tool
        // only collects platform, not placement. Feed/prospecting completion
        // runs materially lower at this length (~20–25% average for a 30s
        // prospecting creative, per the companion guide's worked example), so
        // the verdict and caption name the placement assumption instead of
        // implying every Meta video below 40% is underperforming. `r3540891743`
        return {
          min: 40,
          max: 65,
          rangeText: '40–65%',
          segmentLabel: 'Meta Reels/Stories at 15–30 seconds',
          placementNote:
            'That band is Reels/Stories-specific — feed/prospecting placements typically run materially lower (~20–25% average for a 30-second prospecting creative), so compare against the placement you actually ran.',
          captionNote: 'Reels/Stories placements — feed/prospecting typically runs lower (~20–25% at 30s)',
        }
      }
      return null
    case 'tiktok':
      if (videoLength >= 21 && videoLength <= 34) {
        return {
          min: 55,
          max: 70,
          rangeText: '55–70%',
          segmentLabel: 'TikTok in-feed at 21–34 seconds',
          placementNote:
            'That band reflects standard in-feed distribution — takeover formats like TopView follow different norms, so compare against the distribution you actually ran.',
          captionNote: 'standard in-feed distribution — takeover formats differ',
        }
      }
      return null
    case 'youtube':
      if (videoLength <= 6) {
        // The published 90–95% band exists because bumpers are NON-SKIPPABLE
        // ad inventory — near-total completion is forced. A 6s Short or a
        // skippable/organic upload is not force-completed, so the verdict and
        // caption name the format assumption instead of grading every short
        // YouTube video against a bumper-only source band. `r3541006356`
        return {
          min: 90,
          max: 95,
          rangeText: '90–95%',
          segmentLabel: 'YouTube bumpers (6s, non-skippable ads)',
          placementNote:
            'That band is non-skippable bumper inventory, where completion is effectively forced — Shorts and skippable or organic uploads at this length are not force-completed and typically finish lower, so compare against the format you actually ran.',
          captionNote: 'non-skippable bumpers — Shorts/skippable formats typically finish lower',
        }
      }
      // Exactly 60s belongs to the long-form band — the guide and glossary
      // define long-form as 60s+ at 2–10%. `r3540891745`
      if (videoLength >= 60) {
        return {
          min: 2,
          max: 10,
          rangeText: '2–10%',
          segmentLabel: 'long-form (60s+) skippable in-stream',
          placementNote:
            'That band is skippable in-stream ad inventory — organic uploads and Shorts follow different retention norms, so compare against the format you actually ran.',
          captionNote: 'skippable in-stream ads — organic uploads follow different norms',
        }
      }
      if (videoLength >= 30) {
        return {
          min: 15,
          max: 30,
          rangeText: '15–30%',
          segmentLabel: 'skippable YouTube in-stream at 30–60 seconds',
          placementNote:
            'That band is skippable in-stream ad inventory — organic uploads and Shorts follow different retention norms, so compare against the format you actually ran.',
          captionNote: 'skippable in-stream ads — organic uploads follow different norms',
        }
      }
      return null
    case 'vimeo':
    case 'custom':
      return null
  }
}

/**
 * Verdict copy for the "Complete Views" cards, driven by the published
 * benchmark band for the platform + length. Falls back to honest
 * no-published-benchmark copy instead of inventing a "platform average".
 */
const getCompletionVerdict = (
  platform: CalculatorInputs['platform'],
  videoLength: number,
  completionRate: number
): string => {
  const benchmark = getCompletionBenchmark(platform, videoLength)
  if (!benchmark) {
    return 'No published benchmark band for this platform and length — compare against your own historical average for the same format and placement.'
  }
  const placementSuffix = benchmark.placementNote ? ` ${benchmark.placementNote}` : ''
  if (completionRate > benchmark.max) {
    return `Excellent — above the typical ${benchmark.rangeText} completion band for ${benchmark.segmentLabel}.${placementSuffix}`
  }
  if (completionRate >= benchmark.min) {
    return `In line with the typical ${benchmark.rangeText} completion band for ${benchmark.segmentLabel}.${placementSuffix}`
  }
  return `Below the typical ${benchmark.rangeText} completion band for ${benchmark.segmentLabel}.${placementSuffix || ' Consider refining your content.'}`
}

/**
 * Props for the ResultsSection component
 */
interface ResultsSectionProps {
  /** Calculator results to display */
  results: CalculatorResults
  /** Calculator inputs for context */
  inputs: CalculatorInputs
  /** Function to format time display */
  formatTime: (seconds: number) => string
  
  // Coordinated chart state - managed by parent component for consistency
  /** Currently active chart view */
  activeView: ChartView
  /** Function to change active chart view */
  setActiveView: (view: ChartView) => void
  /** Chart scale (linear or logarithmic) */
  chartScale: 'linear' | 'log'
  /** Function to change chart scale */
  setChartScale: (scale: 'linear' | 'log') => void
  /** Whether to exclude video starts from charts */
  excludeVideoStarts: boolean
  /** Function to toggle excluding video starts */
  setExcludeVideoStarts: (exclude: boolean) => void
  /** Whether to show largest post-hook drop marker */
  showLargestPostHookDrop: boolean
  /** Function to toggle showing largest post-hook drop */
  setShowLargestPostHookDrop: (show: boolean) => void
  
  // Coordinated calculated data - computed once by parent for consistency
  /** Largest post-hook drop-off point data */
  largestPostHookDrop: {
    time: number
    percentage: number
    dropoffRate: number
    label: string
    formattedTime: string
  } | null
  /** Prepared chart data with outlier information embedded and proper typing */
  chartData: {
    retention: RetentionDataPoint[]
    dropoff: DropoffDataPoint[]
  }
}

/**
 * Results section component for displaying analysis results
 * @param {ResultsSectionProps} props - Component props
 * @returns {JSX.Element} Rendered results section
 */
export const ResultsSection = ({
  results,
  inputs,
  formatTime,
  // Coordinated chart state from parent
  activeView,
  setActiveView,
  chartScale,
  setChartScale,
  excludeVideoStarts,
  setExcludeVideoStarts,
  showLargestPostHookDrop,
  setShowLargestPostHookDrop,
  // Coordinated calculated data from parent
  largestPostHookDrop,
  chartData
}: ResultsSectionProps): JSX.Element => {
  // Note: All chart state is now managed by the parent component for proper coordination
  // This ensures consistent state across all chart components and proper data flow




  // Calculate segment analysis. A retention-calc failure is captured into the
  // returned value (pure) and logged from the effect below — logClientError
  // POSTs to /api/log and must not run in the render path (it would re-fire on
  // every re-render for the same bad data).
  const segmentAnalysis = useMemo(() => {
    // Guard clause: Ensure results are available before proceeding
    if (!results) return null

    let retentionError: unknown = null
    const videoLength = inputs.videoLength
    
    // --- NEW Segment Timing Logic --- 
    let hookEndTime: number
    let endStartTime: number
    
    // Better hook timing logic for shorter videos
    if (videoLength < 3) {
      // For extremely short videos, hook is the entire video
      hookEndTime = videoLength
      endStartTime = videoLength
    } else if (videoLength < 20) {
      // For short videos (3-20s), hook is always 3 seconds
      hookEndTime = 3
      
      // End section is ~25% of video length, min 2s, max 6s
      const endDuration = Math.max(2, Math.min(videoLength * 0.25, 6))
      endStartTime = videoLength - endDuration
      
      // Ensure middle section exists with reasonable length
      if (endStartTime <= hookEndTime) {
        // If there's not enough room for all three sections, adjust end section
        endStartTime = Math.min(videoLength - 1, hookEndTime + 1)
      }
    } else if (videoLength <= 60) {
      // For medium videos (20-60s), hook is 6 seconds
      hookEndTime = 6
      // End: Starts 20% before end
      const endDuration = Math.max(10, Math.min(videoLength * 0.20, 30))
      endStartTime = videoLength - endDuration
    } else {
      // Long Videos (> 60s)
      // Hook: 10% of length, min 6s, max 15s
      hookEndTime = Math.max(6, Math.min(videoLength * 0.10, 15))
      // End: Starts 20% before end, min 10s duration, max 30s duration
      const endDuration = Math.max(10, Math.min(videoLength * 0.20, 30))
      endStartTime = videoLength - endDuration
    }
    
    // Ensure hook end time isn't after end start time (for very short videos)
    hookEndTime = Math.min(hookEndTime, endStartTime)
    
    // Define Middle Section based on hook and end
    const middleStartTime = hookEndTime
    const middleEndTime = endStartTime
    const endEndTime = videoLength
    // --- End NEW Segment Timing Logic ---

    // Determine video type based on original length categories for messaging/benchmarks if needed elsewhere
    const isShortVideo = videoLength <= 15
    const isMediumVideo = videoLength > 15 && videoLength <= 45
    const isLongVideo = videoLength > 45
    
    // Calculate retention and drop-off for each segment
    const getRetentionAtTime = (timeInSeconds: number): number => {
      try {
        // Find neighboring breakpoints
        const sortedBreakpoints = [...inputs.breakpoints].sort((a, b) => {
          const aTime = a.isPercentile ? (a.value / 100) * inputs.videoLength : a.value
          const bTime = b.isPercentile ? (b.value / 100) * inputs.videoLength : b.value
          return aTime - bTime
        })
        
        if (sortedBreakpoints.length === 0) {
          return 0 // Return 0 if there are no breakpoints
        }
        
        // Find breakpoints that bound the requested time
        let beforeBreakpoint = sortedBreakpoints[0]
        let afterBreakpoint = sortedBreakpoints[sortedBreakpoints.length - 1]
        
        for (let i = 0; i < sortedBreakpoints.length - 1; i++) {
          const current = sortedBreakpoints[i]
          const next = sortedBreakpoints[i + 1]
          
          if (!current || !next) continue
          
          const currentTime = current.isPercentile 
            ? (current.value / 100) * inputs.videoLength 
            : current.value
          
          const nextTime = next.isPercentile 
            ? (next.value / 100) * inputs.videoLength 
            : next.value
          
          if (currentTime <= timeInSeconds && nextTime >= timeInSeconds) {
            beforeBreakpoint = current
            afterBreakpoint = next
            break
          }
        }
        
        if (!beforeBreakpoint || !afterBreakpoint) {
          return 0
        }
        
        // Interpolate to get retention at exact time
        const beforeTime = beforeBreakpoint.isPercentile 
          ? (beforeBreakpoint.value / 100) * inputs.videoLength 
          : beforeBreakpoint.value
        
        const afterTime = afterBreakpoint.isPercentile
          ? (afterBreakpoint.value / 100) * inputs.videoLength
          : afterBreakpoint.value
        
        // Prevent division by zero
        if (afterTime === beforeTime) {
          return (beforeBreakpoint.viewCount / inputs.totalViews) * 100
        }
        
        // Linear interpolation
        const timeRatio = (timeInSeconds - beforeTime) / (afterTime - beforeTime)
        const viewCount = beforeBreakpoint.viewCount + timeRatio * (afterBreakpoint.viewCount - beforeBreakpoint.viewCount)
        
        return (viewCount / inputs.totalViews) * 100
      } catch (error) {
        retentionError = error
        return 0
      }
    }
    
    // Calculate average drop-off rate in a time range
    const getAverageDropoffInRange = (startTime: number, endTime: number): number => {
      const startRetention = getRetentionAtTime(startTime)
      const endRetention = getRetentionAtTime(endTime)
      const retentionLoss = startRetention - endRetention
      
      // Calculate drop-off percentage relative to viewers at the start of segment
      return startRetention > 0 ? (retentionLoss / startRetention) * 100 : 0
    }
    
    // Calculate metrics for each segment
    const hookRetentionStart = 100
    const hookRetentionEnd = getRetentionAtTime(hookEndTime)
    const hookDropoff = getAverageDropoffInRange(0, hookEndTime)
    
    const middleRetentionStart = hookRetentionEnd
    const middleRetentionEnd = getRetentionAtTime(middleEndTime)
    const middleDropoff = getAverageDropoffInRange(middleStartTime, middleEndTime)
    
    const endRetentionStart = middleRetentionEnd
    const endRetentionEnd = getRetentionAtTime(endEndTime)
    const endDropoff = getAverageDropoffInRange(endStartTime, endEndTime)
    
    // Updated realistic expected retention thresholds for ad videos
    // Evaluate segment performance based on video length and platform
    const evaluateHookPerformance = (): { status: string; message: string } => {
      // Realistic target retention after hook section based on ad industry benchmarks
      // Thumbstop rates (users stopping to watch) typically 15-20% for social ads (based on a 3 second length)
      const targetRetention = isShortVideo 
        ? 25 // For short ad videos (0-15 seconds), 20% retention is strong after initial view
        : isMediumVideo 
          ? 20 // For medium ad videos (15-45 seconds), 20% retention is strong (hook length can be 6 seconds)
          : 18  // For longer ad videos (45-90 seconds), 18% retention is strong (this is where the hook length can exceed 15 seconds)
      
      if (hookRetentionEnd >= targetRetention) {
        return {
          status: "strong",
          message: "Strong hook performance compared to ad industry benchmarks."
        }
      } else if (hookRetentionEnd >= targetRetention * 0.7) {
        return {
          status: "moderate",
          message: "Average hook performance for ad content. Consider strengthening your first 3-5 seconds."
        }
      } else {
        return {
          status: "weak",
          message: "Below average hook performance. Viewers aren't engaging with your opening and leave early."
        }
      }
    }
    
    const evaluateMiddlePerformance = (): { status: string; message: string } => {
      // Middle section expected drop rate for ad videos
      // Most ads see 50-70% of initial viewers drop by midpoint
      const expectedDropoff = isShortVideo 
        ? 30 // Short videos should maintain more viewers
        : isMediumVideo 
          ? 40 // Medium videos have moderate drop
          : 55  // Long videos have higher drop in middle
      
      if (middleDropoff <= expectedDropoff * 0.7) {
        return {
          status: "strong",
          message: "Strong mid-section retention compared to typical ad performance."
        }
      } else if (middleDropoff <= expectedDropoff) {
        return {
          status: "moderate",
          message: "Average mid-section performance. Opportunities to improve engagement. Consider adding pattern interrupts."
        }
      } else {
        return {
          status: "weak",
          message: "High drop-off in middle section. Try shorter scenes or stronger visual elements."
        }
      }
    }
    
    const evaluateEndPerformance = (): { status: string; message: string } => {
      // Evaluating end section retention drop-off specifically, not overall completion
      // For ads, maintaining viewers in the final section (where CTA often appears) is critical
      const expectedEndDropoff = isShortVideo 
        ? 35 // Short ads should have lower drop-off in final section
        : isMediumVideo 
          ? 40 // Medium ads typically see moderate drop-off in final section
          : 50  // Long ads often see higher drop-off in final section
      
      if (endDropoff <= expectedEndDropoff * 0.7) {
        return {
          status: "strong",
          message: "Excellent end section retention. Your CTA and closing are highly effective."
        }
      } else if (endDropoff <= expectedEndDropoff) {
        return {
          status: "moderate",
          message: "Average end section performance for ad content of this length."
        }
      } else {
        return {
          status: "weak",
          message: "High drop-off in end section. Consider a stronger call-to-action or more compelling closing."
        }
      }
    }
    
    const hookEvaluation = evaluateHookPerformance()
    const middleEvaluation = evaluateMiddlePerformance()
    const endEvaluation = evaluateEndPerformance()
    
    return {
      retentionError,
      isShortVideo,
      isMediumVideo,
      isLongVideo,
      sections: {
        hook: {
          startTime: 0,
          endTime: hookEndTime,
          retentionStart: hookRetentionStart,
          retentionEnd: hookRetentionEnd,
          dropoff: hookDropoff,
          evaluation: hookEvaluation
        },
        middle: {
          startTime: middleStartTime,
          endTime: middleEndTime,
          retentionStart: middleRetentionStart,
          retentionEnd: middleRetentionEnd,
          dropoff: middleDropoff,
          evaluation: middleEvaluation
        },
        end: {
          startTime: endStartTime,
          endTime: endEndTime,
          retentionStart: endRetentionStart, 
          retentionEnd: endRetentionEnd,
          dropoff: endDropoff,
          evaluation: endEvaluation
        }
      }
    }
  }, [results, inputs.videoLength, inputs.breakpoints, inputs.totalViews])

  useEffect(() => {
    if (segmentAnalysis?.retentionError != null) {
      logClientError(segmentAnalysis.retentionError, { component: 'ResultsSection', context: 'Error calculating retention' })
    }
  }, [segmentAnalysis])

  // Benchmark-aware completion badge: when a published band exists for this
  // platform + length, grade against it (so the badge can't say "Excellent"
  // for a result the on-page FAQ calls below-benchmark — `r3540594591`);
  // otherwise fall back to the generic 50/30 thresholds.
  const completionBenchmark = getCompletionBenchmark(inputs.platform, inputs.videoLength)
  const completionRateValue = results.advancedMetrics.completionRate
  const completionBadge: { label: string; tone: 'success' | 'warning' | 'error' } = completionBenchmark
    ? completionRateValue > completionBenchmark.max
      ? { label: 'Excellent', tone: 'success' }
      : completionRateValue >= completionBenchmark.min
        ? { label: 'On Benchmark', tone: 'success' }
        : completionBenchmark.placementNote
          ? // Placement-specific band + platform-only input: below-band is a
            // "check your placement" signal, not a failure verdict — feed
            // placements legitimately run below the Reels/Stories band.
            { label: 'Below Benchmark', tone: 'warning' }
          : { label: 'Below Benchmark', tone: 'error' }
    : completionRateValue >= 50
      ? { label: 'Excellent', tone: 'success' }
      : completionRateValue >= 30
        ? { label: 'Good', tone: 'warning' }
        : { label: 'Needs Work', tone: 'error' }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="mt-6 space-y-6 rounded-2xl bg-white p-6 shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800"
        role="region"
        aria-label="Analysis Results"
      >
        <div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
            Video Performance Analysis
          </h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Based on data from {inputs.platform.charAt(0).toUpperCase() + inputs.platform.slice(1)} for a {inputs.videoLength}-second video with {inputs.totalViews.toLocaleString()} initial views
          </p>
        </div>

        {/* View Type and Controls */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          {/* View Type Tabs */}
          <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-700 sm:w-auto">
            <motion.button
              className={cn(
                "px-4 py-2.5 text-sm font-medium -mb-px relative overflow-hidden",
                activeView === 'retention'
                  ? "border-b-2 border-indigo-500 text-indigo-600 dark:text-indigo-400"
                  : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              )}
              onClick={() => setActiveView('retention')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              <span className="flex items-center">
                <RiLineChartLine className="mr-1.5 h-4 w-4" />
                Retention Analysis
              </span>
              {activeView === 'retention' && (
                <motion.div
                  className="absolute bottom-0 left-0 h-0.5 bg-indigo-500 w-full"
                  layoutId="activeViewIndicator"
                />
              )}
            </motion.button>
            <motion.button
              className={cn(
                "px-4 py-2.5 text-sm font-medium -mb-px relative overflow-hidden",
                activeView === 'dropoff'
                  ? "border-b-2 border-indigo-500 text-indigo-600 dark:text-indigo-400"
                  : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              )}
              onClick={() => setActiveView('dropoff')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              <span className="flex items-center">
                <RiBarChartLine className="mr-1.5 h-4 w-4" />
                Drop-off Analysis
              </span>
              {activeView === 'dropoff' && (
                <motion.div
                  className="absolute bottom-0 left-0 h-0.5 bg-indigo-500 w-full"
                  layoutId="activeViewIndicator"
                />
              )}
            </motion.button>
            <motion.button
              className={cn(
                "px-4 py-2.5 text-sm font-medium -mb-px relative overflow-hidden",
                activeView === 'normalizedDropoff'
                  ? "border-b-2 border-orange-500 text-orange-600 dark:text-orange-400"
                  : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              )}
              onClick={() => setActiveView('normalizedDropoff')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              <span className="flex items-center">
                <RiBarChartLine className="mr-1.5 h-4 w-4" />
                Rate Analysis (%/sec)
              </span>
              {activeView === 'normalizedDropoff' && (
                <motion.div
                  className="absolute bottom-0 left-0 h-0.5 bg-orange-500 w-full"
                  layoutId="activeViewIndicator"
                />
              )}
            </motion.button>
          </div>

          {/* Visualization Controls */}
          <div className="flex flex-wrap items-center gap-2 justify-start sm:justify-end w-full sm:w-auto">
            <motion.button
              className={cn(
                "px-3 py-1.5 text-xs rounded-lg border flex items-center gap-1.5 shadow-sm",
                showLargestPostHookDrop
                  ? "border-indigo-300 bg-indigo-50 text-indigo-700 dark:border-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300"
                  : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700/50"
              )}
              onClick={() => setShowLargestPostHookDrop(!showLargestPostHookDrop)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title="Show the largest drop-off point after the initial hook section"
            >
              <div className="h-1.5 w-3 bg-red-500 dark:bg-red-400"></div>
              <span>Largest Post-Hook Drop</span>
            </motion.button>

            <motion.button
              className={cn(
                "px-3 py-1.5 text-xs rounded-lg border flex items-center gap-1.5 shadow-sm",
                excludeVideoStarts
                  ? "border-orange-300 bg-orange-50 text-orange-700 dark:border-orange-700 dark:bg-orange-900/30 dark:text-orange-300"
                  : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700/50"
              )}
              onClick={() => setExcludeVideoStarts(!excludeVideoStarts)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title="Exclude video starts to focus on retention after first breakpoint"
            >
              <div className="h-1.5 w-3 bg-orange-500 dark:bg-orange-400"></div>
              <span>Exclude Video Starts</span>
            </motion.button>

            <motion.button
              className={cn(
                "px-3 py-1.5 text-xs rounded-lg border flex items-center gap-1.5 shadow-sm",
                chartScale === 'log'
                  ? "border-teal-300 bg-teal-50 text-teal-700 dark:border-teal-700 dark:bg-teal-900/30 dark:text-teal-300"
                  : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700/50"
              )}
              onClick={() => setChartScale(chartScale === 'linear' ? 'log' : 'linear')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title="Toggle logarithmic scale for better detail visualization"
            >
              <div className="h-1.5 w-3 bg-teal-500 dark:bg-teal-400"></div>
              <span>Log Scale</span>
            </motion.button>
          </div>
        </div>

        {/* Visualization */}
        <div className="bg-gradient-to-br from-gray-50 to-white p-4 rounded-xl shadow-inner dark:from-gray-800 dark:to-gray-900">
          <div className="h-96 sm:h-[30rem]">
            {activeView === 'retention' ? (
              <RetentionChart
                data={chartData.retention}
                videoLength={inputs.videoLength}
                chartScale={chartScale}
                excludeVideoStarts={excludeVideoStarts}
                showLargestPostHookDrop={showLargestPostHookDrop}
                largestPostHookDrop={largestPostHookDrop}
                breakpoints={inputs.breakpoints}
              />
            ) : activeView === 'dropoff' ? (
              <DropoffChart
                data={chartData.dropoff}
                videoLength={inputs.videoLength}
                chartScale={chartScale}
                excludeVideoStarts={excludeVideoStarts}
                showLargestPostHookDrop={showLargestPostHookDrop}
                largestPostHookDrop={largestPostHookDrop}
                breakpoints={inputs.breakpoints}
              />
            ) : (
              <NormalizedChart
                data={chartData.dropoff}
                videoLength={inputs.videoLength}
                chartScale={chartScale}
                excludeVideoStarts={excludeVideoStarts}
                breakpoints={inputs.breakpoints}
              />
            )}
          </div>
          {/* Chart caption */}
          <div className="mt-3 text-xs text-center text-gray-500 dark:text-gray-400 flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
            <span>
              {activeView === 'retention'
                ? "Retention shows the percentage of initial viewers still watching at each point"
                : activeView === 'dropoff'
                  ? "Drop-off shows the percentage of viewers who left during each segment"
                  : "Normalized Rate shows drop-off percentage adjusted per second for each segment"}
            </span>
            {chartScale === 'log' && (
              <span className="bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-300 px-2 py-0.5 rounded text-xs font-medium">
                Log Scale (reveals detail in lower values)
              </span>
            )}
            {excludeVideoStarts && (
              <span className="bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 px-2 py-0.5 rounded text-xs font-medium">
                Video Starts Excluded
              </span>
            )}
          </div>
        </div>

        {/* Key Metrics */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <RiPercentLine className="mr-2 h-5 w-5 text-indigo-500" />
            Key Performance Metrics
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-blue-50 p-5 dark:from-indigo-900/30 dark:to-blue-900/20 shadow-sm hover:shadow-md transition-shadow duration-300">
              <dt className="flex items-center text-sm font-medium text-indigo-700 dark:text-indigo-300">
                <div className="rounded-full p-2 bg-indigo-100/80 dark:bg-indigo-700/30 mr-3 shadow-sm">
                  <RiVideoLine className="h-5 w-5 text-indigo-500 dark:text-indigo-400" />
                </div>
                Completion Rate
              </dt>
              <dd className="mt-3">
                <div className="flex justify-between items-end mb-1.5">
                  <span className="text-3xl font-bold text-indigo-700 dark:text-indigo-300">
                    {results.advancedMetrics.completionRate.toFixed(1)}%
                  </span>
                  <div className={cn(
                    "text-xs px-2 py-1 rounded-full font-medium",
                    completionBadge.tone === 'success'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                      : completionBadge.tone === 'warning'
                        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
                        : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
                  )}>
                    {completionBadge.label}
                  </div>
                </div>
                <div className="w-full bg-indigo-200/50 dark:bg-indigo-700/30 rounded-full h-2 shadow-inner">
                  <div
                    className="bg-indigo-600 dark:bg-indigo-400 h-2 rounded-full shadow-sm transition-all duration-1000 ease-out"
                    style={{ width: `${Math.min(results.advancedMetrics.completionRate, 100)}%` }}
                  ></div>
                </div>
                <p className="mt-2 text-xs text-indigo-600/80 dark:text-indigo-400/80">
                  {completionBenchmark
                    ? `Viewers who watched to the end — typical band: ${completionBenchmark.rangeText} for ${completionBenchmark.segmentLabel}${completionBenchmark.captionNote ? ` (${completionBenchmark.captionNote})` : ''}`
                    : 'Viewers who watched the entire video'}
                </p>
              </dd>
            </div>

            <div className="rounded-lg bg-gradient-to-br from-violet-50 to-indigo-50 p-5 dark:from-violet-900/30 dark:to-indigo-900/20 shadow-sm hover:shadow-md transition-shadow duration-300">
              <dt className="flex items-center text-sm font-medium text-violet-700 dark:text-violet-300">
                <div className="rounded-full p-2 bg-violet-100/80 dark:bg-violet-700/30 mr-3 shadow-sm">
                  <RiTimeLine className="h-5 w-5 text-violet-500 dark:text-violet-400" />
                </div>
                Average View Duration
              </dt>
              <dd className="mt-3">
                <div className="flex items-end">
                  <span className="text-3xl font-bold text-violet-700 dark:text-violet-300">
                    {results.advancedMetrics.averageViewDuration.toFixed(1)}
                  </span>
                  <span className="text-lg ml-1 mb-0.5 font-medium text-violet-600 dark:text-violet-400">sec</span>
                </div>
                <div className="flex items-center mt-1">
                  <div className="bg-white dark:bg-gray-800 rounded-lg py-1 px-2 text-xs text-violet-700 dark:text-violet-300 font-medium shadow-sm">
                    {Math.round((results.advancedMetrics.averageViewDuration / inputs.videoLength) * 100)}% of total
                  </div>
                  <div className="h-0.5 flex-grow bg-violet-200 dark:bg-violet-800 ml-2 rounded-full">
                    <div className="h-0.5 bg-violet-500 dark:bg-violet-400 rounded-full"
                      style={{ width: `${(results.advancedMetrics.averageViewDuration / inputs.videoLength) * 100}%` }}
                    ></div>
                  </div>
                </div>
                <p className="mt-2 text-xs text-violet-600/80 dark:text-violet-400/80">
                  Average time viewers spent watching
                </p>
              </dd>
            </div>

            <div className="rounded-lg bg-gradient-to-br from-purple-50 to-fuchsia-50 p-5 dark:from-purple-900/30 dark:to-fuchsia-900/20 shadow-sm hover:shadow-md transition-shadow duration-300">
              <dt className="flex items-center text-sm font-medium text-purple-700 dark:text-purple-300">
                <div className="rounded-full p-2 bg-purple-100/80 dark:bg-purple-700/30 mr-3 shadow-sm">
                  <RiPercentLine className="h-5 w-5 text-purple-500 dark:text-purple-400" />
                </div>
                Per-Second Drop-off
              </dt>
              <dd className="mt-3">
                <span className="text-3xl font-bold text-purple-700 dark:text-purple-300">
                  {results.advancedMetrics.perSecondDropoff.toFixed(2)}%
                </span>
                <div className="mt-2 grid grid-cols-10 gap-1">
                  {Array.from({ length: 10 }).map((_, i) => (
                    <div
                      key={i}
                      className="h-1.5 rounded-full"
                      style={{
                        backgroundColor: `rgba(147, 51, 234, ${0.3 + (i / 10) * 0.7})`,
                        opacity: results.advancedMetrics.perSecondDropoff > (i + 1) * 0.2 ? 1 : 0.3
                      }}
                    ></div>
                  ))}
                </div>
                <p className="mt-2 text-xs text-purple-600/80 dark:text-purple-400/80">
                  Average viewer loss rate per second
                </p>
              </dd>
            </div>
          </div>
        </div>

        {/* Platform-Specific Insights */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <RiInformationLine className="mr-2 h-5 w-5 text-indigo-500" />
            Platform-Specific Insights
          </h3>

          <div className="rounded-lg bg-gradient-to-br from-gray-50 to-white p-6 dark:from-gray-800/50 dark:to-gray-900/70 shadow border border-gray-200 dark:border-gray-700">
            <div className="flex items-center mb-4">
              {inputs.platform === 'facebook' && (
                <div className="flex-shrink-0 h-12 w-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mr-4">
                  <RiFacebookCircleLine className="h-7 w-7 text-blue-500 dark:text-blue-400" />
                </div>
              )}
              {inputs.platform === 'tiktok' && (
                <div className="flex-shrink-0 h-12 w-12 bg-black/10 dark:bg-white/10 rounded-full flex items-center justify-center mr-4">
                  <RiTiktokLine className="h-7 w-7 text-black dark:text-white" />
                </div>
              )}
              {inputs.platform === 'youtube' && (
                <div className="flex-shrink-0 h-12 w-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mr-4">
                  <RiYoutubeLine className="h-7 w-7 text-red-600 dark:text-red-400" />
                </div>
              )}
              {inputs.platform === 'vimeo' && (
                <div className="flex-shrink-0 h-12 w-12 bg-sky-100 dark:bg-sky-900/30 rounded-full flex items-center justify-center mr-4">
                  <RiVimeoLine className="h-7 w-7 text-sky-600 dark:text-sky-400" />
                </div>
              )}
              {inputs.platform === 'custom' && (
                <div className="flex-shrink-0 h-12 w-12 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mr-4">
                  <RiLinksLine className="h-7 w-7 text-gray-600 dark:text-gray-300" />
                </div>
              )}

              <div>
                <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                  {inputs.platform.charAt(0).toUpperCase() + inputs.platform.slice(1)} Video Retention Analysis
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {inputs.videoLength} second video with {inputs.totalViews.toLocaleString()} initial views
                </p>
              </div>
            </div>

            <div className="space-y-6">
              {inputs.platform === 'facebook' && (
                <>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div className="p-4 rounded-md bg-white shadow-sm dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">3-Second Views</span>
                        <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                          {(results.retentionRates.find(r => r.breakpoint.id === 'fb-3s')?.value ?? 0).toFixed(1)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 h-1.5 rounded-full">
                        <div
                          className="bg-blue-600 dark:bg-blue-500 h-1.5 rounded-full"
                          style={{ width: `${results.retentionRates.find(r => r.breakpoint.id === 'fb-3s')?.value ?? 0}%` }}
                        ></div>
                      </div>
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        {(results.retentionRates.find(r => r.breakpoint.id === 'fb-3s')?.value ?? 0) > 20
                          ? "Above-average thumbstop rate. Facebook average is 15-20%. Your video captures attention quickly."
                          : "Subpar thumbstop rate. Work on your hook to improve early retention."}
                      </p>
                    </div>

                    <div className="p-4 rounded-md bg-white shadow-sm dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">ThruPlay Views</span>
                        <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                          {(results.retentionRates.find(r => r.breakpoint.id === 'fb-thruplay')?.value ?? 0).toFixed(1)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 h-1.5 rounded-full">
                        <div
                          className="bg-blue-600 dark:bg-blue-500 h-1.5 rounded-full"
                          style={{ width: `${results.retentionRates.find(r => r.breakpoint.id === 'fb-thruplay')?.value ?? 0}%` }}
                        ></div>
                      </div>
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        {(results.retentionRates.find(r => r.breakpoint.id === 'fb-thruplay')?.value ?? 0) > 15
                          ? "Good ThruPlay rate. Facebook will favor your content in the algorithm."
                          : "Consider shortening your video to improve ThruPlay completion rates."}
                      </p>
                    </div>

                    <div className="p-4 rounded-md bg-white shadow-sm dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Complete Views</span>
                        <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                          {results.advancedMetrics.completionRate.toFixed(1)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 h-1.5 rounded-full">
                        <div
                          className="bg-blue-600 dark:bg-blue-500 h-1.5 rounded-full"
                          style={{ width: `${results.advancedMetrics.completionRate}%` }}
                        ></div>
                      </div>
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        {getCompletionVerdict(inputs.platform, inputs.videoLength, results.advancedMetrics.completionRate)}
                      </p>
                    </div>
                  </div>

                  <div className="p-5 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                    <h5 className="font-medium text-blue-800 dark:text-blue-300 mb-2">
                      Facebook-Specific Recommendations
                    </h5>
                    <ul className="space-y-2 text-sm text-blue-700 dark:text-blue-300">
                      <li className="flex items-start">
                        <span className="inline-block h-5 w-5 flex-shrink-0 mr-1.5">•</span>
                        <span>
                          <strong>Thumbstop Optimization:</strong> With average Facebook thumbstop rates at 15-20%, focus on immediate visual impact in first frame to beat the 80% drop in 3 seconds. Your hook is critical; set the tone with movement, text overlays, and visual interest immediately.
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="inline-block h-5 w-5 flex-shrink-0 mr-1.5">•</span>
                        <span>
                          <strong>ThruPlay Optimization:</strong> Facebook&apos;s ThruPlay billing is based on 15-second views or 95% completion.
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="inline-block h-5 w-5 flex-shrink-0 mr-1.5">•</span>
                        <span>
                          <strong>Drop-off Analysis:</strong> Your highest drop-off occurs at {
                            results.dropoffRates.slice(1).reduce((max, current) => current.value > max.value ? current : max, { value: 0, breakpoint: { label: 'N/A' } })?.breakpoint?.label ?? 'N/A'
                          }. Consider revising this section to improve retention.
                        </span>
                      </li>
                    </ul>
                  </div>
                </>
              )}

              {inputs.platform === 'tiktok' && (
                <>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div className="p-4 rounded-md bg-white shadow-sm dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">6-Second Views</span>
                        <span className="text-sm font-medium text-pink-600 dark:text-pink-400">
                          {(results.retentionRates.find(r => r.breakpoint.id === 'tt-6s')?.value ?? 0).toFixed(1)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 h-1.5 rounded-full">
                        <div
                          className="bg-pink-600 dark:bg-pink-500 h-1.5 rounded-full"
                          style={{ width: `${results.retentionRates.find(r => r.breakpoint.id === 'tt-6s')?.value ?? 0}%` }}
                        ></div>
                      </div>
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        {(results.retentionRates.find(r => r.breakpoint.id === 'tt-6s')?.value ?? 0) > 75
                          ? "Excellent early retention. TikTok's algorithm will favor this content."
                          : "Improve your hook to increase early audience retention."}
                      </p>
                    </div>

                    <div className="p-4 rounded-md bg-white shadow-sm dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Watch Time (Avg)</span>
                        <span className="text-sm font-medium text-pink-600 dark:text-pink-400">
                          {results.advancedMetrics.averageViewDuration.toFixed(1)}s
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 h-1.5 rounded-full">
                        <div
                          className="bg-pink-600 dark:bg-pink-500 h-1.5 rounded-full"
                          style={{ width: `${(results.advancedMetrics.averageViewDuration / inputs.videoLength) * 100}%` }}
                        ></div>
                      </div>
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        {(results.advancedMetrics.averageViewDuration / inputs.videoLength) > 0.5
                          ? "Strong average watch time. TikTok rewards high watch time videos."
                          : "Work on keeping viewers engaged longer to increase watch time."}
                      </p>
                    </div>

                    <div className="p-4 rounded-md bg-white shadow-sm dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Complete Views</span>
                        <span className="text-sm font-medium text-pink-600 dark:text-pink-400">
                          {results.advancedMetrics.completionRate.toFixed(1)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 h-1.5 rounded-full">
                        <div
                          className="bg-pink-600 dark:bg-pink-500 h-1.5 rounded-full"
                          style={{ width: `${results.advancedMetrics.completionRate}%` }}
                        ></div>
                      </div>
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        {getCompletionVerdict(inputs.platform, inputs.videoLength, results.advancedMetrics.completionRate)}
                      </p>
                    </div>
                  </div>

                  <div className="p-5 rounded-lg bg-pink-50 dark:bg-pink-900/20 border border-pink-200 dark:border-pink-800">
                    <h5 className="font-medium text-pink-800 dark:text-pink-300 mb-2">
                      TikTok-Specific Recommendations
                    </h5>
                    <ul className="space-y-2 text-sm text-pink-700 dark:text-pink-300">
                      <li className="flex items-start">
                        <span className="inline-block h-5 w-5 flex-shrink-0 mr-1.5">•</span>
                        <span>
                          <strong>Pattern Interrupts:</strong> TikTok&apos;s algorithm favors videos that maintain attention. Use visual changes, effects, or transitions every 2-3 seconds.
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="inline-block h-5 w-5 flex-shrink-0 mr-1.5">•</span>
                        <span>
                          <strong>Loop Potential:</strong> TikTok rewards videos watched multiple times. End with a hook that leads viewers back to the start.
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="inline-block h-5 w-5 flex-shrink-0 mr-1.5">•</span>
                        <span>
                          <strong>Drop-off Analysis:</strong> Your highest drop-off occurs at {
                            results.dropoffRates.slice(1).reduce((max, current) => current.value > max.value ? current : max, { value: 0, breakpoint: { label: 'N/A' } })?.breakpoint?.label ?? 'N/A'
                          }. Consider adding a stronger pattern interrupt at this point.
                        </span>
                      </li>
                    </ul>
                  </div>
                </>
              )}

              {inputs.platform === 'youtube' && (
                <>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div className="p-4 rounded-md bg-white shadow-sm dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Average View Duration</span>
                        <span className="text-sm font-medium text-red-600 dark:text-red-400">
                          {results.advancedMetrics.averageViewDuration.toFixed(1)}s
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 h-1.5 rounded-full">
                        <div
                          className="bg-red-600 dark:bg-red-500 h-1.5 rounded-full"
                          style={{ width: `${(results.advancedMetrics.averageViewDuration / inputs.videoLength) * 100}%` }}
                        ></div>
                      </div>
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        {(results.advancedMetrics.averageViewDuration / inputs.videoLength) > 0.5
                          ? "Strong average watch time. YouTube's algorithm favors this."
                          : "Improve viewer retention to increase watch time."}
                      </p>
                    </div>

                    <div className="p-4 rounded-md bg-white shadow-sm dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">50% Retention Point</span>
                        <span className="text-sm font-medium text-red-600 dark:text-red-400">
                          {(results.retentionRates.find(r => r.breakpoint.id === 'yt-50p')?.value ?? 0).toFixed(1)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 h-1.5 rounded-full">
                        <div
                          className="bg-red-600 dark:bg-red-500 h-1.5 rounded-full"
                          style={{ width: `${results.retentionRates.find(r => r.breakpoint.id === 'yt-50p')?.value ?? 0}%` }}
                        ></div>
                      </div>
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        {(results.retentionRates.find(r => r.breakpoint.id === 'yt-50p')?.value ?? 0) > 50
                          ? "Good mid-point retention compared to platform average."
                          : "Focus on keeping viewers engaged through the middle of your video."}
                      </p>
                    </div>

                    <div className="p-4 rounded-md bg-white shadow-sm dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Complete Views</span>
                        <span className="text-sm font-medium text-red-600 dark:text-red-400">
                          {results.advancedMetrics.completionRate.toFixed(1)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 h-1.5 rounded-full">
                        <div
                          className="bg-red-600 dark:bg-red-500 h-1.5 rounded-full"
                          style={{ width: `${results.advancedMetrics.completionRate}%` }}
                        ></div>
                      </div>
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        {getCompletionVerdict(inputs.platform, inputs.videoLength, results.advancedMetrics.completionRate)}
                      </p>
                    </div>
                  </div>

                  <div className="p-5 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                    <h5 className="font-medium text-red-800 dark:text-red-300 mb-2">
                      YouTube-Specific Recommendations
                    </h5>
                    <ul className="space-y-2 text-sm text-red-700 dark:text-red-300">
                      <li className="flex items-start">
                        <span className="inline-block h-5 w-5 flex-shrink-0 mr-1.5">•</span>
                        <span>
                          <strong>First 15 Seconds:</strong> Present your value proposition clearly in the first 15 seconds. YouTube viewers decide quickly whether to continue watching.
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="inline-block h-5 w-5 flex-shrink-0 mr-1.5">•</span>
                        <span>
                          <strong>Audience Retention:</strong> YouTube heavily weights average watch time in recommendations. Focus on maintaining engagement throughout the entire video.
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="inline-block h-5 w-5 flex-shrink-0 mr-1.5">•</span>
                        <span>
                          <strong>Drop-off Analysis:</strong> Your highest drop-off occurs at {
                            results.dropoffRates.slice(1).reduce((max, current) => current.value > max.value ? current : max, { value: 0, breakpoint: { label: 'N/A' } })?.breakpoint?.label ?? 'N/A'
                          }. Improve this section to increase overall watch time.
                        </span>
                      </li>
                    </ul>
                  </div>
                </>
              )}

              {inputs.platform === 'vimeo' && (
                <>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div className="p-4 rounded-md bg-white shadow-sm dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Average View Duration</span>
                        <span className="text-sm font-medium text-sky-600 dark:text-sky-400">
                          {results.advancedMetrics.averageViewDuration.toFixed(1)}s
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 h-1.5 rounded-full">
                        <div
                          className="bg-sky-600 dark:bg-sky-500 h-1.5 rounded-full"
                          style={{ width: `${(results.advancedMetrics.averageViewDuration / inputs.videoLength) * 100}%` }}
                        ></div>
                      </div>
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        {(results.advancedMetrics.averageViewDuration / inputs.videoLength) > 0.5
                          ? "Strong average watch time for embedded Vimeo viewing."
                          : "Improve viewer retention to lift average watch time."}
                      </p>
                    </div>

                    <div className="p-4 rounded-md bg-white shadow-sm dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">50% Retention Point</span>
                        <span className="text-sm font-medium text-sky-600 dark:text-sky-400">
                          {(results.retentionRates.find(r => r.breakpoint.id === 'vm-50p')?.value ?? 0).toFixed(1)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 h-1.5 rounded-full">
                        <div
                          className="bg-sky-600 dark:bg-sky-500 h-1.5 rounded-full"
                          style={{ width: `${results.retentionRates.find(r => r.breakpoint.id === 'vm-50p')?.value ?? 0}%` }}
                        ></div>
                      </div>
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        {(results.retentionRates.find(r => r.breakpoint.id === 'vm-50p')?.value ?? 0) > 50
                          ? "Good mid-point retention compared to platform average."
                          : "Focus on keeping viewers engaged through the middle of your video."}
                      </p>
                    </div>

                    <div className="p-4 rounded-md bg-white shadow-sm dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Complete Views</span>
                        <span className="text-sm font-medium text-sky-600 dark:text-sky-400">
                          {results.advancedMetrics.completionRate.toFixed(1)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 h-1.5 rounded-full">
                        <div
                          className="bg-sky-600 dark:bg-sky-500 h-1.5 rounded-full"
                          style={{ width: `${results.advancedMetrics.completionRate}%` }}
                        ></div>
                      </div>
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        {getCompletionVerdict(inputs.platform, inputs.videoLength, results.advancedMetrics.completionRate)}
                      </p>
                    </div>
                  </div>

                  <div className="p-5 rounded-lg bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800">
                    <h5 className="font-medium text-sky-800 dark:text-sky-300 mb-2">
                      Vimeo-Specific Recommendations
                    </h5>
                    <ul className="space-y-2 text-sm text-sky-700 dark:text-sky-300">
                      <li className="flex items-start">
                        <span className="inline-block h-5 w-5 flex-shrink-0 mr-1.5">•</span>
                        <span>
                          <strong>Lead with substance:</strong> Vimeo audiences are often higher-intent (embeds, portfolios, client review pages). Open with the payoff rather than a long branded intro.
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="inline-block h-5 w-5 flex-shrink-0 mr-1.5">•</span>
                        <span>
                          <strong>Read the engagement graph:</strong> Use Vimeo&apos;s per-video stats (plays, finishes, and the average-watched curve) to map the 25/50/75/100% points you entered here back to specific moments.
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="inline-block h-5 w-5 flex-shrink-0 mr-1.5">•</span>
                        <span>
                          <strong>Drop-off analysis:</strong> Your highest drop-off occurs at {
                            results.dropoffRates.slice(1).reduce((max, current) => current.value > max.value ? current : max, { value: 0, breakpoint: { label: 'N/A' } })?.breakpoint?.label ?? 'N/A'
                          }. Tighten this section to lift overall completion.
                        </span>
                      </li>
                    </ul>
                  </div>
                </>
              )}

              {inputs.platform === 'custom' && (
                <div className="p-5 rounded-lg bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                  <h5 className="font-medium text-gray-800 dark:text-gray-300 mb-2">
                    Custom Analysis & Recommendations
                  </h5>
                  <div className="space-y-4">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Based on your custom retention data, here are key insights:
                    </p>
                    <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 list-disc pl-5">
                      <li>
                        <strong>Completion Rate:</strong> {results.advancedMetrics.completionRate.toFixed(1)}% of viewers watch your entire video, which is {
                          results.advancedMetrics.completionRate > 35 ? "above average" : "below average"
                        }.
                      </li>
                      <li>
                        <strong>Average View Duration:</strong> {results.advancedMetrics.averageViewDuration.toFixed(1)} seconds ({
                          Math.round((results.advancedMetrics.averageViewDuration / inputs.videoLength) * 100)
                        }% of total length).
                      </li>
                      <li>
                        <strong>Highest Drop-off Point:</strong> {
                          (() => {
                            const maxDropoff = results.dropoffRates.slice(1).reduce(
                              (max, current) => current.value > max.value ? current : max,
                              { value: 0, breakpoint: { label: 'N/A' } }
                            )
                            return `${maxDropoff?.breakpoint?.label ?? 'N/A'} with ${(maxDropoff?.value ?? 0).toFixed(1)}% drop-off rate`
                          })()
                        }
                      </li>
                    </ul>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mt-4">
                      <strong>Recommendation:</strong> Focus on improving your content&apos;s {
                        results.advancedMetrics.completionRate < 30
                          ? "overall engagement"
                          : results.dropoffRates.slice(1).some(item => item.value > 30)
                            ? "high drop-off points"
                            : "opening hook to maximize retention"
                      }.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Segment-Specific Insights */}
        {segmentAnalysis && results !== null && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <RiVideoLine className="mr-2 h-5 w-5 text-indigo-500" />
              Video Segment Analysis
            </h3>

            <div className="rounded-lg bg-gradient-to-br from-gray-50 to-white p-6 dark:from-gray-800/50 dark:to-gray-900/70 shadow border border-gray-200 dark:border-gray-700">
              {/* Overview Card */}
              <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
                <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300 uppercase tracking-wider mb-2">Analysis Summary</h4>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Your {inputs.videoLength}s {segmentAnalysis.isShortVideo ? "short" : segmentAnalysis.isMediumVideo ? "medium" : "long"} video has been analyzed across three key segments.
                  {segmentAnalysis.sections.hook.evaluation.status === "strong" && segmentAnalysis.sections.middle.evaluation.status === "strong" && segmentAnalysis.sections.end.evaluation.status === "strong"
                    ? " All segments show strong retention patterns."
                    : segmentAnalysis.sections.hook.evaluation.status !== "strong" && segmentAnalysis.sections.middle.evaluation.status !== "strong" && segmentAnalysis.sections.end.evaluation.status !== "strong"
                      ? " All segments need improvement to reach optimal retention."
                      : " Some segments perform well while others need optimization."}
                </p>
              </div>

              <div className="space-y-6">
                {/* Section Performance Cards */}
                <div>
                  <h4 className="text-base font-medium text-gray-800 dark:text-gray-200 mb-4 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-indigo-500" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 17L15 12L9 7V17Z" fill="currentColor" />
                    </svg>
                    Section Performance
                  </h4>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    {/* Hook Section Analysis Card */}
                    <div className={cn("rounded-md overflow-hidden shadow-sm border", {
                      "border-green-200 dark:border-green-800": segmentAnalysis.sections.hook.evaluation.status === "strong",
                      "border-yellow-200 dark:border-yellow-800": segmentAnalysis.sections.hook.evaluation.status === "moderate",
                      "border-red-200 dark:border-red-800": segmentAnalysis.sections.hook.evaluation.status === "weak"
                    })}>
                      <div className={cn("p-3", {
                        "bg-green-100 dark:bg-green-900/40": segmentAnalysis.sections.hook.evaluation.status === "strong",
                        "bg-yellow-100 dark:bg-yellow-900/40": segmentAnalysis.sections.hook.evaluation.status === "moderate",
                        "bg-red-100 dark:bg-red-900/40": segmentAnalysis.sections.hook.evaluation.status === "weak"
                      })}>
                        <h4 className={cn("text-base font-medium flex items-center", {
                          "text-green-800 dark:text-green-300": segmentAnalysis.sections.hook.evaluation.status === "strong",
                          "text-yellow-800 dark:text-yellow-300": segmentAnalysis.sections.hook.evaluation.status === "moderate",
                          "text-red-800 dark:text-red-300": segmentAnalysis.sections.hook.evaluation.status === "weak"
                        })}>
                          <span className="inline-block w-2 h-2 rounded-full mr-2 bg-current"></span>
                          Hook Section
                        </h4>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">0s - {formatTime(segmentAnalysis.sections.hook.endTime)}</p>
                      </div>

                      <div className="p-4 bg-white dark:bg-gray-800">
                        <div className="flex justify-between mb-3">
                          <div className="w-1/2 pr-2">
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Total Retention</div>
                            <div className="text-lg font-semibold text-gray-900 dark:text-white">{segmentAnalysis.sections.hook.retentionEnd.toFixed(1)}%</div>
                          </div>
                          <div className="w-1/2 pl-2 border-l border-gray-100 dark:border-gray-700">
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Drop-Off Rate</div>
                            <div className="text-lg font-semibold text-gray-900 dark:text-white">{segmentAnalysis.sections.hook.dropoff.toFixed(1)}%</div>
                          </div>
                        </div>

                        <div className="pt-3 border-t border-gray-100 dark:border-gray-700">
                          <p className="text-sm text-gray-700 dark:text-gray-300">
                            {segmentAnalysis.sections.hook.evaluation.message}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Middle Section Analysis Card */}
                    <div className={cn("rounded-md overflow-hidden shadow-sm border", {
                      "border-green-200 dark:border-green-800": segmentAnalysis.sections.middle.evaluation.status === "strong",
                      "border-yellow-200 dark:border-yellow-800": segmentAnalysis.sections.middle.evaluation.status === "moderate",
                      "border-red-200 dark:border-red-800": segmentAnalysis.sections.middle.evaluation.status === "weak"
                    })}>
                      <div className={cn("p-3", {
                        "bg-green-100 dark:bg-green-900/40": segmentAnalysis.sections.middle.evaluation.status === "strong",
                        "bg-yellow-100 dark:bg-yellow-900/40": segmentAnalysis.sections.middle.evaluation.status === "moderate",
                        "bg-red-100 dark:bg-red-900/40": segmentAnalysis.sections.middle.evaluation.status === "weak"
                      })}>
                        <h4 className={cn("text-base font-medium flex items-center", {
                          "text-green-800 dark:text-green-300": segmentAnalysis.sections.middle.evaluation.status === "strong",
                          "text-yellow-800 dark:text-yellow-300": segmentAnalysis.sections.middle.evaluation.status === "moderate",
                          "text-red-800 dark:text-red-300": segmentAnalysis.sections.middle.evaluation.status === "weak"
                        })}>
                          <span className="inline-block w-2 h-2 rounded-full mr-2 bg-current"></span>
                          Middle Section
                        </h4>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{formatTime(segmentAnalysis.sections.middle.startTime)} - {formatTime(segmentAnalysis.sections.middle.endTime)}</p>
                      </div>

                      <div className="p-4 bg-white dark:bg-gray-800">
                        <div className="flex justify-between mb-3">
                          <div className="w-1/2 pr-2">
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Total Retention</div>
                            <div className="text-lg font-semibold text-gray-900 dark:text-white">{segmentAnalysis.sections.middle.retentionEnd.toFixed(1)}%</div>
                          </div>
                          <div className="w-1/2 pl-2 border-l border-gray-100 dark:border-gray-700">
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Drop-Off Rate</div>
                            <div className="text-lg font-semibold text-gray-900 dark:text-white">{segmentAnalysis.sections.middle.dropoff.toFixed(1)}%</div>
                          </div>
                        </div>

                        <div className="pt-3 border-t border-gray-100 dark:border-gray-700">
                          <p className="text-sm text-gray-700 dark:text-gray-300">
                            {segmentAnalysis.sections.middle.evaluation.message}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* End Section Analysis Card */}
                    <div className={cn("rounded-md overflow-hidden shadow-sm border", {
                      "border-green-200 dark:border-green-800": segmentAnalysis.sections.end.evaluation.status === "strong",
                      "border-yellow-200 dark:border-yellow-800": segmentAnalysis.sections.end.evaluation.status === "moderate",
                      "border-red-200 dark:border-red-800": segmentAnalysis.sections.end.evaluation.status === "weak"
                    })}>
                      <div className={cn("p-3", {
                        "bg-green-100 dark:bg-green-900/40": segmentAnalysis.sections.end.evaluation.status === "strong",
                        "bg-yellow-100 dark:bg-yellow-900/40": segmentAnalysis.sections.end.evaluation.status === "moderate",
                        "bg-red-100 dark:bg-red-900/40": segmentAnalysis.sections.end.evaluation.status === "weak"
                      })}>
                        <h4 className={cn("text-base font-medium flex items-center", {
                          "text-green-800 dark:text-green-300": segmentAnalysis.sections.end.evaluation.status === "strong",
                          "text-yellow-800 dark:text-yellow-300": segmentAnalysis.sections.end.evaluation.status === "moderate",
                          "text-red-800 dark:text-red-300": segmentAnalysis.sections.end.evaluation.status === "weak"
                        })}>
                          <span className="inline-block w-2 h-2 rounded-full mr-2 bg-current"></span>
                          End Section
                        </h4>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{formatTime(segmentAnalysis.sections.end.startTime)} - {formatTime(segmentAnalysis.sections.end.endTime)}</p>
                      </div>

                      <div className="p-4 bg-white dark:bg-gray-800">
                        <div className="flex justify-between mb-3">
                          <div className="w-1/2 pr-2">
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Total Retention</div>
                            <div className="text-lg font-semibold text-gray-900 dark:text-white">{segmentAnalysis.sections.end.retentionEnd.toFixed(1)}%</div>
                          </div>
                          <div className="w-1/2 pl-2 border-l border-gray-100 dark:border-gray-700">
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Drop-Off Rate</div>
                            <div className="text-lg font-semibold text-gray-900 dark:text-white">{segmentAnalysis.sections.end.dropoff.toFixed(1)}%</div>
                          </div>
                        </div>

                        <div className="pt-3 border-t border-gray-100 dark:border-gray-700">
                          <p className="text-sm text-gray-700 dark:text-gray-300">
                            {segmentAnalysis.sections.end.evaluation.message}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Strategic Recommendations */}
                <div>
                  <h4 className="text-base font-medium text-gray-800 dark:text-gray-200 mb-4 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-indigo-500" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9.66347 17H14.3364M11.9999 3V4M18.3639 5.63604L17.6568 6.34315M21 11.9999H20M4 11.9999H3M6.34309 6.34315L5.63599 5.63604M8.46441 15.5356C6.51179 13.5829 6.51179 10.4171 8.46441 8.46449C10.417 6.51187 13.5829 6.51187 15.5355 8.46449C17.4881 10.4171 17.4881 13.5829 15.5355 15.5356L14.9884 16.0827C14.3555 16.7155 13.9999 17.5739 13.9999 18.469V19C13.9999 20.1046 13.1045 21 11.9999 21C10.8954 21 9.99995 20.1046 9.99995 19V18.469C9.99995 17.5739 9.6444 16.7155 9.01151 16.0827L8.46441 15.5356Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Strategic Recommendations
                  </h4>

                  <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-100 dark:border-indigo-800 overflow-hidden">
                    <div className="p-4 border-b border-indigo-100 dark:border-indigo-800">
                      <h5 className="font-medium text-indigo-800 dark:text-indigo-300 mb-2">
                        Video Type: {segmentAnalysis.isShortVideo ? "Short" : segmentAnalysis.isMediumVideo ? "Medium" : "Long"} Format ({inputs.videoLength}s)
                      </h5>
                      <p className="text-sm text-indigo-700 dark:text-indigo-300">
                        {segmentAnalysis.isShortVideo
                          ? "Short videos typically see their steepest drop in the first 3 seconds. Focus on immediate hook and value."
                          : segmentAnalysis.isMediumVideo
                            ? "Medium-length videos follow logarithmic drop-off patterns. Add pattern interrupts every 5-7s to slow the drop."
                            : "Longer videos must justify their length. Expect significant drop-off in the first 10s following a logarithmic curve."
                        }
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-indigo-100 dark:divide-indigo-800">
                      <div className="p-4">
                        <h6 className="text-sm font-medium text-indigo-700 dark:text-indigo-400 mb-3">Hook Optimization</h6>
                        <div className="flex items-start mb-4">
                          <div className="flex-shrink-0 h-6 w-6 rounded-full bg-indigo-100 dark:bg-indigo-800 flex items-center justify-center mr-3">
                            <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300">1</span>
                          </div>
                          <p className="text-sm text-indigo-700 dark:text-indigo-300">
                            {segmentAnalysis.sections.hook.evaluation.message}
                          </p>
                        </div>
                        <div className="flex items-start">
                          <div className="flex-shrink-0 h-6 w-6 rounded-full bg-indigo-100 dark:bg-indigo-800 flex items-center justify-center mr-3">
                            <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300">2</span>
                          </div>
                          <p className="text-sm text-indigo-700 dark:text-indigo-300">
                            {segmentAnalysis.isShortVideo
                              ? "Steep drop-off is normal early; ensure key value/visual is immediate."
                              : segmentAnalysis.isMediumVideo
                                ? "Front-load key messages before the typical 5-10s major drop-off."
                                : "Consider if content can be segmented for better long-form retention."}
                          </p>
                        </div>
                      </div>

                      <div className="p-4">
                        <h6 className="text-sm font-medium text-indigo-700 dark:text-indigo-400 mb-3">Retention Strategy</h6>
                        <div className="flex items-start mb-4">
                          <div className="flex-shrink-0 h-6 w-6 rounded-full bg-indigo-100 dark:bg-indigo-800 flex items-center justify-center mr-3">
                            <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300">3</span>
                          </div>
                          <p className="text-sm text-indigo-700 dark:text-indigo-300">
                            {segmentAnalysis.sections.middle.evaluation.message}
                          </p>
                        </div>
                        <div className="flex items-start">
                          <div className="flex-shrink-0 h-6 w-6 rounded-full bg-indigo-100 dark:bg-indigo-800 flex items-center justify-center mr-3">
                            <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300">4</span>
                          </div>
                          <p className="text-sm text-indigo-700 dark:text-indigo-300">
                            {segmentAnalysis.sections.end.evaluation.message}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  )
}
